// Brand photographs on signed-out surfaces and the widths each is served at. These are our own
// marketing images under `public/images/`, not profile photos: never signed, never behind consent.

// One brand photograph: the shape of its source, and the rungs cut from it.
export type BrandImage = {
  // Path under `public/`, without extension. Its rungs are `<stem>-<width>.webp`.
  stem: string;
  // The source's own pixels. `<img width height>` reserves this aspect; it is never a rung.
  width: number;
  height: number;
  // Ascending, and each strictly narrower than `width` — a rung never upscales its source.
  widths: readonly number[];
  // The rung `src` names, for a client that ignores `srcset`.
  fallback: number;
  // What the slot this image fills asks for, measured against the layout it sits in.
  sizes: string;
};

// A landing roster card (`home.tsx`): square, `object-cover`, so its painted width is what the source must supply.
const ROSTER_CARD =
  "(min-width: 1600px) 740px, (min-width: 768px) calc(50vw - 60px), calc(100vw - 48px)";

// The auth pages' hero (`auth-hero.tsx`). Below `md` it is drawn at `scale-170`; a transform does
// not change the `sizes` choice, so the zoom must be declared here as 170vw.
const AUTH_HERO = "(min-width: 768px) 448px, 170vw";

// Two ladders because the slots differ. Roster cards: 480-2400, `src` names 800. Auth hero:
// 640-2560, with the top rungs for zoomed phones and small tablets, not desktop.
export const BRAND_IMAGES = {
  easternEuropeWomen: {
    stem: "/images/eastern-europe-women",
    width: 2550,
    height: 3825,
    widths: [480, 800, 1200, 1600, 2400],
    fallback: 800,
    sizes: ROSTER_CARD,
  },
  philippineWomen: {
    stem: "/images/philippine-women",
    width: 2731,
    height: 4096,
    widths: [480, 800, 1200, 1600, 2400],
    fallback: 800,
    sizes: ROSTER_CARD,
  },
  signup: {
    stem: "/images/signup",
    width: 2731,
    height: 4096,
    widths: [640, 960, 1280, 1920, 2560],
    fallback: 960,
    sizes: AUTH_HERO,
  },
} as const satisfies Record<string, BrandImage>;

// The image in the shape `ProfileImage` renders (`src`, `srcset` rungs, source aspect). Reuses it
// so `srcset` and priority preloading are written in one place.
export function brandPhoto(image: BrandImage, alt: string) {
  return {
    url: `${image.stem}-${image.fallback}.webp`,
    width: image.width,
    height: image.height,
    alt,
    sources: image.widths.map((width) => ({ url: `${image.stem}-${width}.webp`, width })),
  };
}
